import { useEffect, useId, useState, type RefObject } from 'react'

export type InPageNavItem = {
  /** The id of the heading or section the link points to. */
  id: string
  /** The link text, usually the heading text. */
  label: string
}

export type InPageNavProps = {
  /** The sections to list. When left out, the h2 headings with an id inside `container` are read. */
  items?: InPageNavItem[]
  /** The element holding the page content; its h2 headings become the list when `items` is left out. */
  container?: RefObject<HTMLElement | null>
  /** The heading above the list and the name of the nav landmark. */
  title?: string
}

function readHeadings(root: HTMLElement | null | undefined): InPageNavItem[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLHeadingElement>('h2[id]')).map((heading) => ({
    id: heading.id,
    label: heading.textContent?.trim() ?? '',
  }))
}

/**
 * An "On this page" list of the page's sections. The link for the section in
 * view carries aria-current="location", updated as the page scrolls.
 */
export default function InPageNav({ items, container, title = 'On this page' }: InPageNavProps) {
  const [read, setRead] = useState<InPageNavItem[]>([])
  const list = items ?? read
  const [current, setCurrent] = useState<string | undefined>(undefined)
  const titleId = useId()
  const ids = list.map((item) => item.id).join(' ')

  useEffect(() => {
    if (items) return
    const found = readHeadings(container?.current)
    // Headings live in the DOM, which is only readable once mounted.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRead(found)
  }, [items, container])

  useEffect(() => {
    const order = ids.split(' ').filter(Boolean)
    if (!order.length || typeof IntersectionObserver === 'undefined') return
    const targets = order.map((id) => document.getElementById(id)).filter((node): node is HTMLElement => Boolean(node))
    const visible = new Set<string>()
    // A section counts as in view while its heading sits in the top third of the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }
        const first = order.find((id) => visible.has(id))
        if (first) setCurrent(first)
      },
      { rootMargin: '0px 0px -66% 0px' },
    )
    targets.forEach((target) => observer.observe(target))
    return () => observer.disconnect()
  }, [ids])

  if (!list.length) return null

  return (
    <nav className="mds-in-page-nav" aria-labelledby={titleId}>
      <p className="mds-in-page-nav__title" id={titleId}>
        {title}
      </p>
      <ul className="mds-in-page-nav__list">
        {list.map((item) => (
          <li key={item.id}>
            <a
              className="mds-in-page-nav__link"
              href={`#${item.id}`}
              aria-current={item.id === current ? 'location' : undefined}
              onClick={() => setCurrent(item.id)}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
